import React, { useContext, useEffect, useState } from "react";
import { Template } from "../../types/Template";
import { MESSAGE_TYPES, Messages, MessageType } from "../../constants/Messages";
import AlertBanner from "../generic/AlertBanner";
import FormComponent from "../generic/FormComponent";
import SubmitButton from "../generic/SubmitButton";
import InputField from "../generic/InputField";
import AddAnotherItemButton from "../generic/AddAnotherItemButton";
import { fetchTemplateDetails, Material, TemplateDetailsResponse, updateTemplate } from "../../services/TemplateAPI";
import MaterialRow from "./MaterialRow";
import Modal from "../generic/Modal";
import { UserContext } from "../../context/UserContext";

interface EditTemplateFormProps {
    templateId: number;
    onTemplateUpdated: (updatedTemplate: Template) => void;
    onClose: () => void;
}

const EditTemplateForm: React.FC<EditTemplateFormProps> = ({
                                                               templateId,
                                                               onTemplateUpdated,
                                                               onClose,
                                                           }) => {
    const [ templateDetails, setTemplateDetails ] = useState<TemplateDetailsResponse | null>(null);
    const [ originalDetails, setOriginalDetails ] = useState<TemplateDetailsResponse | null>(null);
    const [ alert, setAlert ] = useState<{ message: string; type: MessageType, title?: string } | null>(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ loading, setLoading ] = useState(true);
    const [ shouldCloseModal, setShouldCloseModal ] = useState(false);
    const { isLoggedIn } = useContext(UserContext);

    // Fetch template details on component mount or when `templateId` changes
    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const response = await fetchTemplateDetails(templateId);
                setTemplateDetails(response);
                setOriginalDetails(response);
            } catch (error) {
                setAlert({
                    message: Messages.ERROR_FETCH_TEMPLATE,
                    type: MESSAGE_TYPES.ERROR,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [ templateId, isLoggedIn ]);

    // Handle field changes for the template
    const handleFieldChange = (field: keyof Template, value: any) => {
        if (templateDetails) {
            setTemplateDetails({ ...templateDetails, [field]: value });
        }
    };

    // Handle changes in individual material rows
    const handleMaterialRowChange = (index: number, field: keyof Material, value: any) => {
        if (templateDetails) {
            const updatedMaterials = [ ...templateDetails.materials ];
            updatedMaterials[index] = { ...updatedMaterials[index], [field]: value };
            setTemplateDetails({ ...templateDetails, materials: updatedMaterials });
        }
    };

    // Add a new material row
    const addNewRow = () => {
        if (templateDetails) {
            const newMaterial: Material = {
                material_type_id: null,
                material_size_id: null,
                quantity: 0,
            };
            setTemplateDetails({
                ...templateDetails,
                materials: [ ...templateDetails.materials, newMaterial ],
            });
        }
    };

    // Submit changes to update the template
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (templateDetails) {
                // Validate template name
                if (!templateDetails.name.trim()) {
                    throw new Error(Messages.ERROR_TEMPLATE_NAME);
                }

                // Validate materials
                const invalidMaterials = templateDetails.materials.some(
                    (material) =>
                        material.material_type_id === null ||
                        material.material_size_id === null ||
                        isNaN(parseFloat(material.quantity.toString())) ||
                        parseFloat(material.quantity.toString()) <= 0 ||
                        material.quantity_unit === "",
                );

                if (invalidMaterials) {
                    throw new Error(Messages.ERROR_MATERIALS);
                }

                // Update the template
                const updatedTemplate = await updateTemplate(templateId, {
                    name: templateDetails.name,
                    materials: templateDetails.materials.map((material) => ({
                        material_type_id: material.material_type_id,
                        material_size_id: material.material_size_id,
                        quantity: material.quantity,
                        quantity_unit: material.quantity_unit,
                    })),
                });

                // Show success alert
                setAlert({
                    message: Messages.SUCCESS_UPDATE_TEMPLATE,
                    type: MESSAGE_TYPES.SUCCESS,
                });

                // Update parent component with the updated template
                onTemplateUpdated(updatedTemplate.productTemplate);

                // Delay closing the modal and clearing the alert
                setTimeout(() => {
                    setAlert(null); // Clear the alert after showing it
                    setShouldCloseModal(true); // Only set this after alert is shown
                }, 1000); // Show the alert for 1.5 seconds before closing the modal
            }
        } catch (error: any) {
            setAlert({
                title: Messages.ERROR_UPDATE_TEMPLATE,
                message: error.message || Messages.ERROR_UPDATE_TEMPLATE,
                type: MESSAGE_TYPES.ERROR,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Determine if the template has been modified
    const isModified = JSON.stringify(templateDetails) !== JSON.stringify(originalDetails);

    // Close the modal if `shouldCloseModal` is set
    useEffect(() => {
        if (shouldCloseModal) {
            onClose();
        }
    }, [ shouldCloseModal, onClose ]);

    return (
        <Modal onClose={onClose}>
            <FormComponent title="Edit Template" isModal={true}>
                {alert && (
                    <AlertBanner
                        message={alert.message}
                        message_type={alert.type}
                        onClose={() => setAlert(null)}
                    />
                )}

                {loading ? (
                    <p className="text-center text-gray-600">Loading template details...</p>
                ) : templateDetails ? (
                    <div className="space-y-4">
                        <InputField
                            type="text"
                            value={templateDetails.name}
                            onChange={(value) => handleFieldChange("name", value)}
                            label="Template Name"
                            placeholder="Enter template name"
                            required
                        />

                        {templateDetails.materials.map((material, index) => (
                            <MaterialRow
                                key={index}
                                row={material}
                                index={index}
                                onChange={handleMaterialRowChange}
                                onRemove={() => {
                                    const updatedMaterials = templateDetails.materials.filter(
                                        (_, i) => i !== index,
                                    );
                                    setTemplateDetails({ ...templateDetails, materials: updatedMaterials });
                                }}
                                showRemoveButton={templateDetails.materials.length > 1}
                            />
                        ))}

                        <AddAnotherItemButton className={"w-full"} onAdd={addNewRow}/>

                        <div className="flex justify-end space-x-4">
                            <button
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <SubmitButton
                                isLoading={isSubmitting}
                                disabled={!isModified || isSubmitting}
                                onClick={handleSubmit}
                            >
                                Save Changes
                            </SubmitButton>
                        </div>
                    </div>
                ) : (
                    <p className="text-center text-red-600">
                        Failed to load template details. Please refresh the page.
                    </p>
                )}
            </FormComponent>
        </Modal>
    );
};

export default EditTemplateForm;
